// src/components/modals/CreateWarehouseProductionModal.jsx
import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { X, Package, Calendar, FileText, Warehouse as WarehouseIcon, ChefHat } from 'lucide-react'
import toast from 'react-hot-toast'
import WarehouseProductionService from '../../services/warehouseProductionService'
import BranchService from '../../services/branchService'
import ProductService from '../../services/productService'
import RecipesService from '../../services/recipesService'

const CreateWarehouseProductionModal = ({ isOpen, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false)
  const [warehouses, setWarehouses] = useState([])
  const [products, setProducts] = useState([])
  const [recipes, setRecipes] = useState([])
  const [finishedProducts, setFinishedProducts] = useState([])
  const [formData, setFormData] = useState({
    warehouse: '',
    recipe: '',
    targetProduct: '',
    quantityProduced: '',
    expiryDate: '',
    note: ''
  })
  const [errors, setErrors] = useState({})

  useEffect(() => {
    if (isOpen) {
      fetchWarehouses()
      fetchProducts()
      fetchRecipes()
      fetchFinishedProducts()
      resetForm()
    }
  }, [isOpen])

  const fetchWarehouses = async () => {
    try {
      const response = await BranchService.getAllBranches()
      if (response.success && response.data) {
        const branchList = response.data.branches || response.data
        // Filter only warehouse type branches
        const warehouseList = branchList.filter(b => b.type === 'warehouse' && b.isActive)
        setWarehouses(warehouseList)
      }
    } catch (error) {
      console.error('Error fetching warehouses:', error)
      toast.error('Failed to load warehouses')
    }
  }

  const fetchProducts = async () => {
    try {
      const response = await ProductService.getAllProducts()
      if (response.success && response.data) {
        const productList = response.data.products || response.data
        // Filter only recipe-based products
        const recipeProducts = productList.filter(p => p.type === 'recipe_based' && p.isActive)
        setProducts(recipeProducts)
      }
    } catch (error) {
      console.error('Error fetching products:', error)
      toast.error('Failed to load products')
    }
  }

  const fetchRecipes = async () => {
    try {
      const response = await RecipesService.getAllRecipes({ page: 1, limit: 1000 })
      if (response.success && response.data) {
        const recipeList = response.data.recipes || response.data.docs || response.data
        setRecipes(Array.isArray(recipeList) ? recipeList : [])
      }
    } catch (error) {
      console.error('Error fetching recipes:', error)
      toast.error('Failed to load recipes')
    }
  }

  const fetchFinishedProducts = async () => {
    try {
      const response = await ProductService.getAllProducts()
      if (response.success && response.data) {
        const productList = response.data.products || response.data
        // Filter only products with type "product" (not raw materials)
        const activeProducts = productList.filter(p => p.isActive && p.type === 'product')
        setFinishedProducts(activeProducts)
      }
    } catch (error) {
      console.error('Error fetching finished products:', error)
      toast.error('Failed to load finished products')
    }
  }

  const resetForm = () => {
    setFormData({
      warehouse: '',
      recipe: '',
      targetProduct: '',
      quantityProduced: '',
      expiryDate: '',
      note: ''
    })
    setErrors({})
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.warehouse) {
      newErrors.warehouse = 'Warehouse is required'
    }

    if (!formData.recipe) {
      newErrors.recipe = 'Recipe is required'
    }

    if (!formData.targetProduct) {
      newErrors.targetProduct = 'Finished product is required'
    }

    if (!formData.quantityProduced) {
      newErrors.quantityProduced = 'Quantity is required'
    } else if (parseFloat(formData.quantityProduced) <= 0) {
      newErrors.quantityProduced = 'Quantity must be greater than 0'
    }

    if (!formData.expiryDate) {
      newErrors.expiryDate = 'Expiry date is required'
    } else {
      const selectedDate = new Date(formData.expiryDate)
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      if (selectedDate < today) {
        newErrors.expiryDate = 'Expiry date cannot be in the past'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    try {
      setLoading(true)

      const productionData = {
        warehouse: formData.warehouse,
        recipe: formData.recipe,
        targetProduct: formData.targetProduct,
        quantityProduced: parseFloat(formData.quantityProduced),
        expiryDate: formData.expiryDate,
        ...(formData.note && { note: formData.note })
      }

      const response = await WarehouseProductionService.createWarehouseProduction(productionData)

      if (response.success) {
        toast.success(response.message || 'Warehouse production created successfully')
        onSuccess()
      }
    } catch (error) {
      console.error('Error creating warehouse production:', error)
      toast.error(error.response?.data?.message || 'Failed to create warehouse production')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  if (!isOpen) return null

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" 
      style={{ zIndex: 999999 }}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Create Warehouse Production</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Warehouse Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <WarehouseIcon className="inline h-4 w-4 mr-1" />
              Warehouse *
            </label>
            <select
              name="warehouse"
              value={formData.warehouse}
              onChange={handleChange}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                errors.warehouse ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">Select Warehouse</option>
              {warehouses.map((warehouse) => (
                <option key={warehouse._id} value={warehouse._id}>
                  {warehouse.name}
                </option>
              ))}
            </select>
            {errors.warehouse && (
              <p className="mt-1 text-sm text-red-600">{errors.warehouse}</p>
            )}
          </div>

          {/* Recipe Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <ChefHat className="inline h-4 w-4 mr-1" />
              Recipe *
            </label>
            <select
              name="recipe"
              value={formData.recipe}
              onChange={handleChange}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                errors.recipe ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">Select Recipe</option>
              {recipes.map((recipe) => (
                <option key={recipe._id} value={recipe._id}>
                  {recipe.name} {recipe.product?.name ? `(${recipe.product.name})` : ''}
                </option>
              ))}
            </select>
            {errors.recipe && (
              <p className="mt-1 text-sm text-red-600">{errors.recipe}</p>
            )}
          </div>

          {/* Target Product (Finished Product) Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Package className="inline h-4 w-4 mr-1" />
              Finished Product *
            </label>
            <select
              name="targetProduct"
              value={formData.targetProduct}
              onChange={handleChange}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                errors.targetProduct ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">Select Finished Product</option>
              {finishedProducts.map((product) => (
                <option key={product._id} value={product._id}>
                  {product.name} ({product.sku})
                </option>
              ))}
            </select>
            {errors.targetProduct && (
              <p className="mt-1 text-sm text-red-600">{errors.targetProduct}</p>
            )}
          </div>

          {/* Quantity Produced */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Package className="inline h-4 w-4 mr-1" />
              Quantity Produced *
            </label>
            <input
              type="number"
              name="quantityProduced"
              value={formData.quantityProduced}
              onChange={handleChange}
              min="1"
              step="1"
              placeholder="Enter quantity"
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                errors.quantityProduced ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.quantityProduced && (
              <p className="mt-1 text-sm text-red-600">{errors.quantityProduced}</p>
            )}
          </div>

          {/* Expiry Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar className="inline h-4 w-4 mr-1" />
              Expiry Date *
            </label>
            <input
              type="date"
              name="expiryDate"
              value={formData.expiryDate}
              onChange={handleChange}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
                errors.expiryDate ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.expiryDate && (
              <p className="mt-1 text-sm text-red-600">{errors.expiryDate}</p>
            )}
          </div>

          {/* Note */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FileText className="inline h-4 w-4 mr-1" />
              Note (Optional)
            </label>
            <textarea
              name="note"
              value={formData.note}
              onChange={handleChange}
              rows={3}
              placeholder="Enter any additional notes..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
            <p className="mt-1 text-xs text-gray-500">Note: This cannot be updated later</p>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating...' : 'Create Production'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

CreateWarehouseProductionModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired
}

export default CreateWarehouseProductionModal
